/**
 * Shared TypeScript types for SubContractorHub field mapping (Story SCH-2.1).
 *
 * Mirrors the backend Pydantic models in
 * `joules-admin-backend/app/services/subcontractorhub/models.py` and the
 * endpoints in `app/api/v1/endpoints/integrations/subcontractorhub.py`.
 *
 * Backend uses snake_case JSON; the fieldMappingService converts to
 * camelCase at the boundary. This is the camelCase view consumed by hooks
 * and components.
 */

// ─── D17 (architecture.md lines 444-463) ─────────────────────────────────
// Frontend duplicates this constant for client-side type-mismatch warning
// logic (AC4). Backend's app/services/subcontractorhub/field_mapper.py is
// the source of truth; this is a stable, small, intentional duplication.
export const FIELD_TYPE_MAP: Readonly<Record<string, string>> = Object.freeze({
  date_time: 'DATE_INPUT',
  date: 'DATE_INPUT',
  dropdown: 'RADIOBUTTON',
  checkbox: 'CHECKBOX',
  file: 'FILE_UPLOAD',
  text: 'TEXT_INPUT',
  text_area: 'TEXT_INPUT',
  currency: 'NUMBER',
});

export const HIGH_CONFIDENCE_MIN = 0.85;
export const LOW_CONFIDENCE_MIN = 0.5;

/**
 * Pure-function predicate: is the given (SCH type, Joules type) pair compatible?
 * Used by FieldMappingTab to gate the per-row Confirm button on type mismatch.
 */
export function isTypeCompatible(
  schFieldType: string,
  joulesTargetType: string,
): boolean {
  const expected = FIELD_TYPE_MAP[schFieldType];
  return expected !== undefined && expected === joulesTargetType;
}

/**
 * SCH-BUG-7b (P-E2-4): is this SCH field type one Joules recognizes (in the D17
 * map)? An UNRECOGNIZED type isn't a "type mismatch" with the chosen target —
 * it's a type we can't map at all — so the UI should say so distinctly.
 */
export function isKnownSchFieldType(schFieldType: string): boolean {
  return FIELD_TYPE_MAP[schFieldType] !== undefined;
}

// ─── SCH custom field (from /v2/get-custom-fields/project) ───────────────
export interface SchCustomField {
  fieldKey: string;
  fieldLabel: string;
  fieldType: string; // one of 8 D17 keys (date_time, date, dropdown, ...)
  options: string[] | null; // populated for dropdown/radio types
}

// ─── Joules checklist-item candidate (from checklist_items) ──────────────
export interface JoulesChecklistCandidate {
  id: number;
  label: string;
  itemType: string; // DATE_INPUT, RADIOBUTTON, CHECKBOX, FILE_UPLOAD, TEXT_INPUT, NUMBER
  templateId: number | null;
  /** Joined from checklist_templates.name. Used by the picker to
   * disambiguate items like "Completed Date" that appear in many
   * checklists. NULL when the parent checklist has no name or no parent
   * (orphaned item).
   */
  templateName: string | null;
}

// ─── Auto-match suggestion (one per SCH field) ───────────────────────────
export interface AutoMatchResult {
  joulesTargetId: number;
  confidence: number; // [0, 1]
}

// ─── Field-mapping row (one row of tenant_integration_field_mappings) ────
export type JoulesTargetType = 'checklist_item' | 'project_column' | 'skip';
export type WritePolicy =
  | 'always_overwrite'
  | 'overwrite_unless_modified'
  | 'never_overwrite';

export interface FieldMappingRow {
  id: string; // UUID
  tenantIntegrationId: string; // UUID
  externalFieldKey: string;
  externalFieldLabel: string;
  externalFieldType: string;
  joulesTargetType: JoulesTargetType;
  joulesTargetId: number | null;
  joulesTargetColumn: string | null;
  enabled: boolean;
  precedence: number;
  writePolicy: WritePolicy;
  displayFormat: string | null;
  notes: string | null;
  isDormant: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

// ─── /discover-catalog response ──────────────────────────────────────────
export interface DiscoverCatalogResponse {
  schFields: SchCustomField[];
  joulesCandidates: JoulesChecklistCandidate[];
  // Keyed by external_field_key. Value is null when no candidate scored
  // above LOW_CONFIDENCE_MIN.
  suggestions: Record<string, AutoMatchResult | null>;
  existingMappings: FieldMappingRow[];
  schemaFingerprint: string;
  schemaDriftDetected: boolean;
}

export type DiscoverCatalogErrorCode =
  | 'integration_not_found'
  | 'auth_failed'
  | 'connection_test_failed'
  | 'internal_error';

export interface DiscoverCatalogErrorObject {
  code: DiscoverCatalogErrorCode;
  message: string;
  details?: Record<string, unknown>;
}

export type DiscoverCatalogResult =
  | { data: DiscoverCatalogResponse; error: null }
  | { data: null; error: DiscoverCatalogErrorObject };

// ─── /mappings/field POST (single UPSERT) ────────────────────────────────
export interface CreateFieldMappingRequest {
  externalFieldKey: string;
  externalFieldLabel: string;
  externalFieldType: string;
  joulesTargetType: JoulesTargetType;
  joulesTargetId?: number | null;
  joulesTargetColumn?: string | null;
  writePolicy?: WritePolicy;
  displayFormat?: string | null;
  notes?: string | null;
}

export type CreateFieldMappingErrorCode =
  | 'integration_not_found'
  | 'mapping_conflict'
  | 'internal_error';

export interface CreateFieldMappingErrorObject {
  code: CreateFieldMappingErrorCode;
  message: string;
  details?: Record<string, unknown>;
}

export type CreateFieldMappingResult =
  | { data: FieldMappingRow; error: null }
  | { data: null; error: CreateFieldMappingErrorObject };

// ─── /mappings/field/bulk POST (atomic INSERT) ───────────────────────────
export interface BulkCreateFieldMappingsRequest {
  rows: CreateFieldMappingRequest[];
}

export interface BulkCreateFieldMappingsSuccess {
  rows: FieldMappingRow[];
}

export type BulkCreateFieldMappingsResult =
  | { data: BulkCreateFieldMappingsSuccess; error: null }
  | { data: null; error: CreateFieldMappingErrorObject };

// ─── /mappings/field/{id} PATCH ──────────────────────────────────────────
export interface UpdateFieldMappingRequest {
  joulesTargetType?: JoulesTargetType;
  joulesTargetId?: number | null;
  joulesTargetColumn?: string | null;
  enabled?: boolean;
  precedence?: number;
  writePolicy?: WritePolicy;
  displayFormat?: string | null;
  notes?: string | null;
  isDormant?: boolean;
}

export type UpdateFieldMappingResult =
  | { data: FieldMappingRow; error: null }
  | { data: null; error: CreateFieldMappingErrorObject };

// ─── /mappings/field/reorder PATCH ───────────────────────────────────────
export interface ReorderEntry {
  id: string;
  precedence: number;
}

export interface ReorderFieldMappingsRequest {
  ordering: ReorderEntry[];
}

export interface ReorderFieldMappingsSuccess {
  updated: number;
}

export type ReorderFieldMappingsResult =
  | { data: ReorderFieldMappingsSuccess; error: null }
  | { data: null; error: CreateFieldMappingErrorObject };

// ─── /mappings/field/{id} DELETE ─────────────────────────────────────────
export interface DeleteFieldMappingSuccess {
  deletedId: string;
}

export type DeleteFieldMappingResult =
  | { data: DeleteFieldMappingSuccess; error: null }
  | { data: null; error: CreateFieldMappingErrorObject };
